import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { toast } from 'react-toastify';
import isEqual from 'lodash.isequal';
import {
  DEFAULT_THEME_IDS,
  Preset,
  accentById,
  backgroundById,
  fontById,
  normalizeSocials,
  primaryById,
} from '@porter/shared-theme';
import useUser from '../../../hooks/global/useUser';
import useOffsetTop from '../../../hooks/ui/useOffsetTop';
import useGetBookingSite from '../../../hooks/businesses/useGetBookingSite';
import useUpdateBookingSite from '../../../hooks/businesses/useUpdateBookingSite';
import useGetGallery from '../../../hooks/gallery/useGetGallery';
import useGetFaqs from '../../../hooks/faqs/useGetFaqs';
import { isValidFaq } from '../../../helpers/faqs';
import { rejectedSocials } from '../../../helpers/booking-site';
import { CUSTOMER_WEB_URL } from '../../../constants/global';
import { LoadedUser } from '../../../interfaces';
import {
  BookingSiteDraft,
  BookingSiteSocials,
  UpdateBookingSitePayload,
} from '../../../types/bookingSite';
import { GalleryContext } from '../../../types/gallery';
import GallerySection from '../../gallery/GallerySection';
import useGalleryUploads from '../../gallery/useGalleryUploads';
import FaqSection from './FaqSection';
import Accordion from './Accordion';
import PresetGrid from './PresetGrid';
import BackgroundPicker from './BackgroundPicker';
import PrimaryPicker from './PrimaryPicker';
import AccentPicker from './AccentPicker';
import FontPicker from './FontPicker';
import LogoSection from './LogoSection';
import SocialsSection from './SocialsSection';
import QrSection from './QrSection';
import SitePreview from './SitePreview';
import useBookingSiteDraft from './useBookingSiteDraft';
import { contrastNotices } from './contrast';
import {
  LINE,
  LIVE_BG,
  LIVE_DOT,
  SETTINGS_WIDTH,
  resetButton,
} from './constants';

/**
 * Unselected ids are omitted, so an unchosen slot stays null on the record and
 * resolves to the Porter default rather than being frozen at publish.
 */
function toPayload(draft: BookingSiteDraft): UpdateBookingSitePayload {
  const {
    instagramHandle,
    tiktokHandle,
    facebookUrl,
    websiteUrl,
    presetId,
    backgroundId,
    primaryId,
    accentId,
    fontId,
  } = draft;
  const ids = { backgroundId, primaryId, accentId, fontId };
  const chosen = Object.fromEntries(
    Object.entries(ids).filter(([, value]) => !!value),
  );
  return {
    ...chosen,
    // Only alongside a selection — on its own a null presetId would create an
    // empty theme row and report an untouched shop as customized.
    ...(Object.keys(chosen).length ? { presetId } : {}),
    // Publish is reachable straight from a focused field, so the blur that
    // normally tidies it never ran.
    ...normalizeSocials({
      instagramHandle,
      tiktokHandle,
      facebookUrl,
      websiteUrl,
    }),
  };
}

type SectionKey =
  | 'background'
  | 'primary'
  | 'accent'
  | 'font'
  | 'logo'
  | 'photos'
  | 'faqs'
  | 'socials'
  | 'qr';

function BookingSiteSettings() {
  const { user } = useUser<LoadedUser>();
  const { settings, isLoading } = useGetBookingSite();
  const [open, setOpen] = useState<SectionKey | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  // The logo saves outside the draft, so the preview needs a nudge to refetch.
  const [refreshToken, setRefreshToken] = useState(0);

  const muiTheme = useTheme();
  const isWide = useMediaQuery(muiTheme.breakpoints.up('lg'));
  const [containerRef, offsetTop, onOffsetChange] = useOffsetTop();

  const businessId = user?.business?.id;
  const galleryContext: GalleryContext | null = businessId
    ? { kind: 'booking-site', businessId }
    : null;
  const {
    images: gallery,
    isLoading: isGalleryLoading,
    isError: isGalleryError,
    refetch: refetchGallery,
  } = useGetGallery(galleryContext);
  const galleryOrEmpty = gallery ?? (isGalleryError ? [] : undefined);
  const galleryUploads = useGalleryUploads(galleryContext);

  const {
    faqs,
    isLoading: isFaqsLoading,
    isError: isFaqsError,
    refetch: refetchFaqs,
  } = useGetFaqs(businessId ?? null);
  const faqsOrEmpty = faqs ?? (isFaqsError ? [] : undefined);

  const { draft, saved, isDirty, setDraft, clearDraft } = useBookingSiteDraft(
    businessId,
    settings,
    galleryOrEmpty,
    faqsOrEmpty,
  );

  // Memoised because the preview posts on identity change — rebuilt every
  // render, a keystroke would re-post and make customer-web re-resolve.
  const themeIds = useMemo(
    () => ({
      backgroundId: draft?.backgroundId ?? DEFAULT_THEME_IDS.backgroundId,
      primaryId: draft?.primaryId ?? DEFAULT_THEME_IDS.primaryId,
      accentId: draft?.accentId ?? DEFAULT_THEME_IDS.accentId,
      fontId: draft?.fontId ?? DEFAULT_THEME_IDS.fontId,
    }),
    [draft?.backgroundId, draft?.primaryId, draft?.accentId, draft?.fontId],
  );

  const socials = useMemo(
    () => ({
      instagramHandle: draft?.instagramHandle ?? null,
      tiktokHandle: draft?.tiktokHandle ?? null,
      facebookUrl: draft?.facebookUrl ?? null,
      websiteUrl: draft?.websiteUrl ?? null,
    }),
    [
      draft?.instagramHandle,
      draft?.tiktokHandle,
      draft?.facebookUrl,
      draft?.websiteUrl,
    ],
  );

  const galleryImageRefs = useMemo(
    () => draft?.gallery.map(image => image.imageRef) ?? [],
    [draft?.gallery],
  );
  const previewGallery = isGalleryError ? null : galleryImageRefs;
  const previewFaqs = useMemo(
    () =>
      isFaqsError
        ? null
        : draft?.faqs.map(({ id, header, text }) => ({
            faqId: id,
            header,
            text,
          })) ?? null,
    [draft?.faqs, isFaqsError],
  );
  const isGalleryDirty = !isEqual(draft?.gallery, saved?.gallery);
  const isFaqsDirty = !isEqual(draft?.faqs, saved?.faqs);
  const [rejected, setRejected] = useState<(keyof BookingSiteSocials)[]>([]);
  const [showFaqErrors, setShowFaqErrors] = useState(false);

  const { mutate: publish, isPending } = useUpdateBookingSite({
    onFaqsCommitted: committed => setDraft({ faqs: committed }),
    onSuccess: () => {
      setRejected([]);
      clearDraft();
      toast.success('Your booking site is live!');
    },
    onError: error => {
      const fields = rejectedSocials(error);
      setRejected(fields);
      return fields.length > 0;
    },
  });

  if (isLoading || isGalleryLoading || isFaqsLoading || !settings || !draft) {
    return null;
  }

  const onPublish = () => {
    if (isFaqsDirty && isFaqsError) {
      toast.error(
        'Could not load your existing FAQs. Reload the page before publishing FAQ changes.',
      );
      return;
    }

    if (isFaqsDirty && draft.faqs.some(faq => !isValidFaq(faq))) {
      setShowFaqErrors(true);
      toast.error('Fill in every FAQ before publishing.');
      return;
    }

    if (isGalleryDirty && isGalleryError) {
      toast.error(
        'Could not load your existing photos. Reload the page before publishing photo changes.',
      );
      return;
    }

    publish({
      settings: toPayload(draft),
      ...(isGalleryDirty ? { galleryImageRefs } : {}),
      ...(isFaqsDirty ? { faqs: draft.faqs } : {}),
    });
  };

  const siteUrl =
    settings.scope === 'artist'
      ? `${CUSTOMER_WEB_URL}/artist/${settings.urlPath}`
      : `${CUSTOMER_WEB_URL}/shop/${settings.urlPath}`;
  const displayUrl = siteUrl.replace(/^https?:\/\//, '');

  // An untouched shop shows no ring and previews the real, unthemed site. The
  // card mini-renders still need a full theme, hence the defaults below.
  const hasSelection =
    !!draft.backgroundId ||
    !!draft.primaryId ||
    !!draft.accentId ||
    !!draft.fontId;

  const toggle = (key: SectionKey) => setOpen(open === key ? null : key);

  const selectPreset = (preset: Preset) =>
    setDraft({
      backgroundId: preset.bg,
      primaryId: preset.primary,
      accentId: preset.accent,
      fontId: preset.font,
      presetId: preset.id,
    });

  // presetId follows from the four ids, so it is never set here.
  const selectOption = (
    key: 'backgroundId' | 'primaryId' | 'accentId' | 'fontId',
    id: string,
  ) => setDraft({ [key]: id });

  const socialCount = [
    draft.instagramHandle,
    draft.tiktokHandle,
    draft.facebookUrl,
    draft.websiteUrl,
  ].filter(Boolean).length;

  const previewTheme = settings.customized || hasSelection ? themeIds : null;
  const notices = previewTheme ? contrastNotices(previewTheme) : [];

  const preview = (
    <SitePreview
      url={siteUrl}
      // Nothing chosen and nothing published: leave the real page alone.
      theme={previewTheme}
      notices={notices}
      socials={socials}
      gallery={previewGallery}
      faqs={previewFaqs}
      isDirty={isDirty}
      refreshToken={refreshToken}
    />
  );

  return (
    <Box
      ref={(el: HTMLElement | null) => {
        containerRef.current = el;
        if (el) onOffsetChange(el);
      }}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: 'stretch',
        minHeight: 0,
        height: { lg: `calc(100vh - ${offsetTop}px)` },
        overflow: { lg: 'hidden' },
      }}>
      <Box
        sx={{
          width: { xs: '100%', lg: SETTINGS_WIDTH },
          flex: 'none',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          borderRight: { lg: `1px solid ${LINE}` },
        }}>
        <Box sx={{ padding: '22px 24px 14px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}>
            <Typography
              variant="caption"
              component="p"
              fontWeight={600}
              sx={{ color: 'primary.main', marginBottom: '6px' }}>
              Shop settings
            </Typography>
            {!isWide && (
              <Box
                component="button"
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                sx={{
                  ...resetButton,
                  color: 'primary.main',
                  typography: 'h5',
                  fontWeight: 700,
                }}>
                {showPreview ? 'Hide preview' : 'Show preview'}
              </Box>
            )}
          </Box>
          <Typography
            variant="h1"
            component="h2"
            sx={{ margin: '0 0 6px', color: 'text.primary' }}>
            Booking site
          </Typography>
          <Typography
            variant="body2"
            sx={{
              margin: '0 0 14px',
              lineHeight: 1.45,
              color: 'text.secondary',
            }}>
            Customize the page clients land on from your booking link. Changes
            show in the preview and go out when you publish.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
            }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 10px',
                borderRadius: '50px',
                background: LIVE_BG,
                color: 'success.main',
                typography: 'caption',
                fontWeight: 600,
              }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: LIVE_DOT,
                }}
              />
              Live
            </Box>
            <Typography
              variant="subtitle2"
              sx={{ color: 'text.primary', wordBreak: 'break-all' }}>
              {displayUrl}
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(siteUrl);
                toast.success('Link copied!');
              }}
              sx={{
                ...resetButton,
                color: 'primary.main',
                typography: 'h5',
                fontWeight: 700,
              }}>
              Copy link
            </Box>
          </Box>
        </Box>

        {!isWide && showPreview && (
          <Box sx={{ height: 560, display: 'flex', mb: 2 }}>{preview}</Box>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: { lg: 'auto' },
            padding: '4px 24px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}>
          <PresetGrid presetId={draft.presetId} onSelect={selectPreset} />

          <Accordion
            title="Background"
            value={
              draft.backgroundId
                ? backgroundById(draft.backgroundId)?.name
                : 'Not set'
            }
            swatch={
              draft.backgroundId
                ? backgroundById(draft.backgroundId)?.css
                : undefined
            }
            open={open === 'background'}
            onToggle={() => toggle('background')}>
            <BackgroundPicker
              theme={themeIds}
              selectedId={draft.backgroundId}
              onSelect={id => selectOption('backgroundId', id)}
            />
          </Accordion>

          <Accordion
            title="Primary color"
            value={
              draft.primaryId ? primaryById(draft.primaryId)?.name : 'Not set'
            }
            swatch={
              draft.primaryId ? primaryById(draft.primaryId)?.hex : undefined
            }
            open={open === 'primary'}
            onToggle={() => toggle('primary')}>
            <PrimaryPicker
              theme={themeIds}
              primaryId={draft.primaryId}
              onSelect={id => selectOption('primaryId', id)}
            />
          </Accordion>

          <Accordion
            title="Accent color"
            value={
              draft.accentId ? accentById(draft.accentId)?.name : 'Not set'
            }
            swatch={
              draft.accentId ? accentById(draft.accentId)?.hex : undefined
            }
            open={open === 'accent'}
            onToggle={() => toggle('accent')}>
            <AccentPicker
              theme={themeIds}
              accentId={draft.accentId}
              onSelect={id => selectOption('accentId', id)}
            />
          </Accordion>

          <Accordion
            title="Font"
            value={draft.fontId ? fontById(draft.fontId)?.name : 'Not set'}
            open={open === 'font'}
            onToggle={() => toggle('font')}>
            <FontPicker
              fontId={draft.fontId}
              onSelect={id => selectOption('fontId', id)}
            />
          </Accordion>

          <Accordion
            title="Photos"
            value={
              isGalleryError
                ? 'Could not load'
                : `${draft.gallery.length} photo${
                    draft.gallery.length === 1 ? '' : 's'
                  }`
            }
            open={open === 'photos'}
            onToggle={() => toggle('photos')}>
            {isGalleryError ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Could not load photos.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => refetchGallery()}>
                  Retry
                </Button>
              </Box>
            ) : (
              <GallerySection
                images={draft.gallery}
                onChange={images => setDraft({ gallery: images })}
                uploads={galleryUploads}
                disabled={isPending}
                dirty={isGalleryDirty}
                leadCount={settings.scope === 'artist' ? 1 : 5}
                tileMinWidth={96}
                helpText={
                  settings.scope === 'artist'
                    ? 'Photos of your work, in the order clients see them. Drag to reorder, and the first photo leads your profile page.'
                    : 'These fill the header collage on your booking site. The first photo takes the big slot, the next four fill the smaller ones, and the rest sit behind Show all photos.'
                }
                emptyDetail="Add photos customers will see here. The first one leads."
              />
            )}
          </Accordion>

          <Accordion
            title="Logo"
            tag="Web only"
            value={settings.logoUrl ? 'Uploaded' : 'Not set'}
            open={open === 'logo'}
            onToggle={() => toggle('logo')}>
            <LogoSection
              businessId={businessId || ''}
              employeeId={settings.employeeId}
              logoUrl={settings.logoUrl}
              onSaved={() => setRefreshToken(token => token + 1)}
            />
          </Accordion>

          <Accordion
            title="FAQs"
            value={
              isFaqsError
                ? 'Could not load'
                : `${draft.faqs.length} FAQ${
                    draft.faqs.length === 1 ? '' : 's'
                  }`
            }
            open={open === 'faqs'}
            onToggle={() => toggle('faqs')}>
            {isFaqsError ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Could not load FAQs.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => refetchFaqs()}>
                  Retry
                </Button>
              </Box>
            ) : (
              <FaqSection
                faqs={draft.faqs}
                onChange={nextFaqs => setDraft({ faqs: nextFaqs })}
                disabled={isPending}
                showErrors={showFaqErrors}
              />
            )}
          </Accordion>

          <Accordion
            title="Socials"
            value={socialCount ? `${socialCount} linked` : 'None'}
            open={open === 'socials'}
            onToggle={() => toggle('socials')}>
            <SocialsSection
              socials={draft}
              rejected={rejected}
              onChange={changes => {
                setDraft(changes);
                // Editing is an answer to the complaint; the next publish
                // decides again.
                setRejected(current =>
                  current.filter(field => !(field in changes)),
                );
              }}
            />
          </Accordion>

          <Accordion
            title="QR code"
            value={displayUrl}
            open={open === 'qr'}
            onToggle={() => toggle('qr')}>
            <QrSection url={siteUrl} />
          </Accordion>
        </Box>

        {isDirty && (
          <Box
            sx={{
              flex: 'none',
              minHeight: 76,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              padding: '0 24px',
              borderTop: `1px solid ${LINE}`,
              background: '#fff',
              position: { xs: 'sticky', lg: 'static' },
              bottom: 0,
            }}>
            <Typography variant="body2" sx={{ color: 'text.tertiary' }}>
              Unpublished changes
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Box
                component="button"
                type="button"
                disabled={isPending}
                onClick={clearDraft}
                sx={{
                  ...resetButton,
                  padding: '12px 20px',
                  borderRadius: '50px',
                  color: 'text.secondary',
                  typography: 'h4',
                  fontWeight: 700,
                }}>
                Discard
              </Box>
              <Box
                component="button"
                type="button"
                disabled={isPending}
                onClick={onPublish}
                sx={{
                  ...resetButton,
                  padding: '13px 32px',
                  borderRadius: '50px',
                  bgcolor: 'primary.main',
                  color: 'common.white',
                  '&:hover': { background: 'rgb(230, 40, 5)' },
                  '&:disabled': { opacity: 0.6 },
                  typography: 'h4',
                  fontWeight: 700,
                }}>
                {isPending ? 'Publishing…' : 'Publish'}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {isWide && preview}
    </Box>
  );
}

export default BookingSiteSettings;
